import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Typography, DatePicker, Table, Space, Spin, Empty, Select, Checkbox, Button, Segmented, message, Input, InputNumber, Tooltip } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
dayjs.extend(isoWeek);
import * as XLSX from 'xlsx';
import api from '../api';

const DOW_ORDER = { Monday: 0, Tuesday: 1, Wednesday: 2, Thursday: 3, Friday: 4, Saturday: 5, Sunday: 6 };

function weekdaysInMonth(monthKey) {
  if (!monthKey) return 0;
  const [y, m] = String(monthKey).split('-').map(Number);
  const date = new Date(y, m - 1, 1);
  let count = 0;
  while (date.getMonth() === m - 1) {
    const dow = date.getDay();
    if (dow !== 0 && dow !== 6) count++;
    date.setDate(date.getDate() + 1);
  }
  return count;
}

function groupLabel(gk, groupBy) {
  if (!gk) return '';
  if (groupBy === 'dow') return gk;
  if (groupBy === 'week' || groupBy === 'week_sum') {
    const mon = dayjs(gk);
    const sun = mon.add(6, 'day');
    return `${mon.format('MMM D')} – ${sun.format('MMM D')}`;
  }
  const s = String(gk).replace('-', '');
  return dayjs(s, 'YYYYMM').format('MMM YYYY');
}

function fmtHrs(v) {
  if (v == null || v === 0) return '—';
  return Number(v).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

const billable    = r => r ? (r['Reg'] || 0) + (r['OT'] || 0) + (r['Hol1.5'] || 0) : 0;
const nonBillable = (r, codes) => r ? codes.reduce((sum, c) => sum + (r[c] || 0), 0) - billable(r) : 0;

// Odd Data: checks run on each employee's day. d.Total sums every earning
// code; d.Worked is Reg + OT + Hol1.5;
// d.Scheduled is the Paycor shift's hours (unpaid lunch already taken out),
// null with no shift that day.
// Each returns the issue text, or null when the day is fine.
const ODD_SCHEDULE_CUSHION = 0.5;   // hours allowed past the schedule
const ODD_UNSCHEDULED_WORK_MAX = 0.5;   // Reg + OT + Hol1.5 allowed with no shift
const ODD_UNSCHEDULED_TOTAL_MAX = 8;    // all codes allowed with no shift
const ODD_DAY_CHECKS = [
  (d) => (d.Scheduled != null && Math.round((d.Total - d.Scheduled) * 100) / 100 > ODD_SCHEDULE_CUSHION
    ? `${fmtHrs(d.Total - d.Scheduled)} hrs over schedule` : null),
  (d) => (d.Scheduled == null && Math.round(d.Worked * 100) / 100 > ODD_UNSCHEDULED_WORK_MAX
    ? `${fmtHrs(d.Worked)} hrs worked with no schedule` : null),
  (d) => (d.Scheduled == null && Math.round(d.Total * 100) / 100 > ODD_UNSCHEDULED_TOTAL_MAX
    ? `Over ${ODD_UNSCHEDULED_TOTAL_MAX} hrs with no schedule` : null),
];

// Years and months from a hire date to today, e.g. "3y 9m" -- same as the
// agent summary header.
const fmtTenure = (d) => {
  if (!d) return null;
  const ms  = Date.now() - new Date(d).getTime();
  const yrs = Math.floor(ms / (365.25 * 24 * 3600 * 1000));
  const mos = Math.floor((ms % (365.25 * 24 * 3600 * 1000)) / (30.44 * 24 * 3600 * 1000));
  return yrs > 0 ? `${yrs}y ${mos}m` : `${mos}m`;
};

export default function TimecardSummaryPage() {
  const [fromMonth,      setFromMonth]      = useState(() => dayjs().subtract(2, 'month').startOf('month'));
  const [toMonth,        setToMonth]        = useState(() => dayjs().startOf('month'));
  const [groupBy,        setGroupBy]        = useState('month');
  const [rows,           setRows]           = useState([]);
  const [detailRows,     setDetailRows]     = useState([]);
  const [codes,          setCodes]          = useState([]);
  const [scheduledHrs,   setScheduledHrs]   = useState({});
  const [loading,        setLoading]        = useState(false);
  const [deptFilter,     setDeptFilter]     = useState([]);
  const [jobFilter,      setJobFilter]      = useState([]);
  const [bucketTraining, setBucketTraining] = useState(true);
  const [flaggedOnly,    setFlaggedOnly]    = useState(false);
  const [hrsThreshold,   setHrsThreshold]   = useState(40);
  const [thresholdDir,   setThresholdDir]   = useState('under'); // 'under' | 'over'
  const [pivotView,      setPivotView]      = useState(false);
  const [agentSearch,    setAgentSearch]    = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/timecards/summary', {
        params: {
          from_month:      fromMonth.format('YYYYMM'),
          to_month:        toMonth.format('YYYYMM'),
          bucket_training: bucketTraining ? '1' : '0',
          // By Agent aggregates the detail rows itself, so it asks for weekly
          // detail and rolls it up client-side rather than adding a backend mode
          group_by:        groupBy === 'agent' ? 'week_sum' : groupBy === 'odd' ? 'day' : groupBy,
        },
      });
      setRows(r.data.rows || []);
      setDetailRows(r.data.detail_rows || []);
      setCodes(r.data.earning_codes || []);
      setScheduledHrs(r.data.scheduled_hrs || {});
    } catch {
      setRows([]); setDetailRows([]); setCodes([]); setScheduledHrs({});
    } finally {
      setLoading(false);
    }
  }, [fromMonth, toMonth, bucketTraining, groupBy]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const deptOptions = useMemo(() => {
    const vals = [...new Set(rows.map(r => r.DeptCode).filter(Boolean))].sort();
    return vals.map(v => ({ label: v, value: v }));
  }, [rows]);

  const jobOptions = useMemo(() => {
    const vals = [...new Set(rows.map(r => r.JobName).filter(Boolean))].sort();
    return vals.map(v => ({ label: v, value: v }));
  }, [rows]);

  const visibleRows = useMemo(() => {
    let out = rows;
    if (deptFilter.length) out = out.filter(r => deptFilter.includes(r.DeptCode));
    if (jobFilter.length)  out = out.filter(r => jobFilter.includes(r.JobName));
    return out;
  }, [rows, deptFilter, jobFilter]);

  // ── Exports ─────────────────────────────────────────────────────────────────
  const xlsxExport = (wsRows, sheetName, filename) => {
    if (!wsRows.length) { message.warning('No data to export.'); return; }
    const ws = XLSX.utils.json_to_sheet(wsRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, filename);
  };

  const fileTag    = `${fromMonth.format('YYYYMM')}_${toMonth.format('YYYYMM')}`;
  const groupLabel3rd = groupBy === 'dow' ? 'Day of Week' : groupBy === 'week_sum' ? 'Week' : 'Month';

  const exportSummary = () => {
    // By Agent renders its own rolled-up rows, so export those rather than the
    // weekly rows the fetch happened to request
    if (groupBy === 'agent') {
      const wsRows = visibleAgentRows.map(r => {
        const row = {
          'Agent':        r.AgentName,
          'Emp #':        r.EmployeeNumber,
          'Team':         r.TeamName,
          'FT/PT':        r.EmploymentType,
          'Tenure':       fmtTenure(r.HireDate) ?? '',
          'Billable':     billable(r) || '',
          'Non-Billable': nonBillable(r, codes) || '',
        };
        codes.forEach(c => { row[c] = r[c] ?? ''; });
        return row;
      });
      xlsxExport(wsRows, 'By Agent', `timecard_by_agent_${fileTag}.xlsx`);
      return;
    }
    const wsRows = visibleRows.map(r => {
      const row = {
        'Dept Code':    r.DeptCode,
        'Team':         r.TeamName,
        'Job':          r.JobName,
        [groupLabel3rd]: groupLabel(r.GroupKey, groupBy),
      };
      if (groupBy === 'month') row['Forecasted'] = (r.Forecasted ?? billable(r)) || '';
      row['Billable']     = billable(r) || '';
      row['Non-Billable'] = nonBillable(r, codes) || '';
      codes.forEach(c => { row[c] = r[c] ?? ''; });
      return row;
    });
    xlsxExport(wsRows, 'Summary', `timecard_summary_${fileTag}.xlsx`);
  };

  const exportDetail = () => {
    let out = detailRows;
    if (deptFilter.length) out = out.filter(r => deptFilter.includes(r.DeptCode));
    if (jobFilter.length)  out = out.filter(r => jobFilter.includes(r.JobName));
    const wsRows = out.map(r => {
      const row = {
        'Agent':          r.AgentName,
        'Emp #':          r.EmployeeNumber,
        'Dept Code':      r.DeptCode,
        'Team':           r.TeamName,
        'Job':            r.JobName,
        [groupLabel3rd]:  groupLabel(r.GroupKey, groupBy),
        'Billable':       billable(r) || '',
        'Non-Billable':   nonBillable(r, codes) || '',
      };
      codes.forEach(c => { row[c] = r[c] ?? ''; });
      return row;
    });
    xlsxExport(wsRows, 'Detail', `timecard_detail_${fileTag}.xlsx`);
  };

  // ── By Agent: one row per agent for the whole range ──────────────────────────
  // Team comes from the DepartmentCode on each punch, so an agent who changed
  // departments mid-range has rows under two teams. Grouping on EmployeeNumber
  // alone keeps their hours together, and the team shown is the most recent one
  // -- preferring a real team over '(No Team)', since an unmapped department
  // code should not outrank a known assignment just by being later.
  const agentRows = useMemo(() => {
    if (groupBy !== 'agent') return [];
    let src = detailRows;
    if (deptFilter.length) src = src.filter(r => deptFilter.includes(r.DeptCode));
    if (jobFilter.length)  src = src.filter(r => jobFilter.includes(r.JobName));

    const map = {};
    for (const r of src) {
      const key = r.EmployeeNumber;
      if (!map[key]) {
        map[key] = {
          EmployeeNumber: key,
          AgentName:      r.AgentName,
          TeamName:       null,
          EmploymentType: r.EmploymentType,
          HireDate:       r.HireDate,
          _teamKey:       null,   // GroupKey the shown team came from
        };
      }
      const t = map[key];
      codes.forEach(c => {
        if (r[c] != null) t[c] = Math.round(((t[c] || 0) + r[c]) * 100) / 100;
      });

      const realTeam = r.TeamName && r.TeamName !== '(No Team)';
      const better   = t.TeamName == null
                    || (realTeam && (t.TeamName === '(No Team)' || r.GroupKey >= t._teamKey))
                    || (!realTeam && t.TeamName === '(No Team)' && r.GroupKey >= t._teamKey);
      if (better) { t.TeamName = r.TeamName; t._teamKey = r.GroupKey; }
    }
    return Object.values(map).sort((a, b) =>
      (a.AgentName || '').localeCompare(b.AgentName || ''));
  }, [groupBy, detailRows, deptFilter, jobFilter, codes]);

  // Name search over the rolled-up rows. Applied here rather than in the table
  // so the pinned Total reflects what is on screen rather than the full set.
  const visibleAgentRows = useMemo(() => {
    const q = agentSearch.trim().toLowerCase();
    if (!q) return agentRows;
    return agentRows.filter(r => (r.AgentName || '').toLowerCase().includes(q));
  }, [agentRows, agentSearch]);

  // ── Weekly pivot: one row per agent, one column per week ─────────────────────
  const weeklyPivot = useMemo(() => {
    if (groupBy !== 'week') return { rows: [], weeks: [] };
    let src = detailRows;
    if (deptFilter.length) src = src.filter(r => deptFilter.includes(r.DeptCode));
    if (jobFilter.length)  src = src.filter(r => jobFilter.includes(r.JobName));

    const weekSet = new Set(src.map(r => r.GroupKey));
    const weeks   = [...weekSet].sort();

    const agentMap = {};
    src.forEach(r => {
      const key = r.EmployeeNumber;
      if (!agentMap[key]) {
        agentMap[key] = {
          key,
          AgentName:      r.AgentName,
          TeamName:       r.TeamName,
          JobName:        r.JobName,
          DeptCode:       r.DeptCode,
          EmploymentType: r.EmploymentType,
        };
      }
      // Sum all earning codes toward the weekly total
      const hrs = codes.reduce((sum, c) => sum + (r[c] || 0), 0);
      agentMap[key][r.GroupKey] = (agentMap[key][r.GroupKey] || 0) + hrs;
    });

    let rows = Object.values(agentMap).sort((a, b) =>
      (a.TeamName || '').localeCompare(b.TeamName || '') ||
      (a.AgentName || '').localeCompare(b.AgentName || '')
    );

    const isFlagged = (hrs) =>
      thresholdDir === 'under' ? hrs < hrsThreshold : hrs > hrsThreshold;

    const currentWeekStart = dayjs().startOf('isoWeek').format('YYYY-MM-DD');
    rows.forEach(r => {
      r._flaggedWeeks = weeks.filter(wk => wk !== currentWeekStart && (r[wk] || 0) > 0 && isFlagged(r[wk] || 0)).length;
      r._activeWeeks  = weeks.filter(wk => wk !== currentWeekStart && (r[wk] || 0) > 0).length;
    });

    if (flaggedOnly) {
      rows = rows.filter(r => r._flaggedWeeks > 0);
    }

    return { rows, weeks, isFlagged };
  }, [groupBy, detailRows, deptFilter, jobFilter, flaggedOnly, hrsThreshold, thresholdDir, codes]);

  const exportWeekly = () => {
    if (!weeklyPivot.rows.length) { message.warning('No data to export.'); return; }
    const wsRows = weeklyPivot.rows.map(r => {
      const row = { Agent: r.AgentName, Team: r.TeamName, Job: r.JobName, 'FT/PT': r.EmploymentType || '' };
      weeklyPivot.weeks.forEach(wk => { row[groupLabel(wk, 'week')] = r[wk] ?? 0; });
      return row;
    });
    xlsxExport(wsRows, 'Weekly', `timecard_weekly_${fileTag}.xlsx`);
  };

  // ── Odd Data: employee-days that fail a check ───────────────────────────────
  // Totals are over all of the day's rows (every team and job) before the Dept
  // and Job filters, so a filter narrows the list without shrinking a day.
  const oddRows = useMemo(() => {
    if (groupBy !== 'odd') return [];
    const days = {};
    detailRows.forEach(r => {
      const key = `${r.EmployeeNumber}|${r.GroupKey}`;
      if (!days[key]) {
        days[key] = { key, Date: r.GroupKey, EmployeeNumber: r.EmployeeNumber, AgentName: r.AgentName,
                      _teams: new Set(), _jobs: new Set(), _depts: new Set(), Total: 0 };
      }
      const d = days[key];
      d._teams.add(r.TeamName); d._jobs.add(r.JobName); d._depts.add(r.DeptCode);
      codes.forEach(c => {
        if (r[c]) { d[c] = (d[c] || 0) + r[c]; d.Total += r[c]; }
      });
    });
    return Object.values(days)
      .map(d => {
        const day = { ...d, Scheduled: scheduledHrs[d.key] ?? null, Worked: billable(d),
                      TeamName: [...d._teams].join(', '), JobName: [...d._jobs].join(', ') };
        return { ...day, Issues: ODD_DAY_CHECKS.map(chk => chk(day)).filter(Boolean) };
      })
      .filter(d => d.Issues.length
        && (!deptFilter.length || deptFilter.some(x => d._depts.has(x)))
        && (!jobFilter.length  || jobFilter.some(x => d._jobs.has(x))))
      .sort((a, b) => b.Date.localeCompare(a.Date) || (a.AgentName || '').localeCompare(b.AgentName || ''));
  }, [groupBy, detailRows, codes, scheduledHrs, deptFilter, jobFilter]);

  const exportOdd = () => {
    xlsxExport(oddRows.map(r => {
      const row = { Date: r.Date, Agent: r.AgentName, 'Emp #': r.EmployeeNumber, Team: r.TeamName, Job: r.JobName };
      codes.forEach(c => { row[c] = r[c] ?? ''; });
      row.Total = Math.round(r.Total * 100) / 100;
      row.Scheduled = r.Scheduled ?? '';
      row.Issue = r.Issues.join('; ');
      return row;
    }), 'Odd Data', `timecard_odd_data_${fileTag}.xlsx`);
  };

  // ── Columns ──────────────────────────────────────────────────────────────────
  const groupCol = groupBy === 'dow'
    ? {
        title: 'Day of Week', dataIndex: 'GroupKey', key: 'GroupKey', width: 120,
        sorter: (a, b) => (DOW_ORDER[a.GroupKey] ?? 9) - (DOW_ORDER[b.GroupKey] ?? 9),
        defaultSortOrder: 'ascend',
        render: v => v,
      }
    : groupBy === 'week_sum'
    ? {
        title: 'Week', dataIndex: 'GroupKey', key: 'GroupKey', width: 140,
        sorter: (a, b) => String(a.GroupKey).localeCompare(String(b.GroupKey)),
        defaultSortOrder: 'ascend',
        render: v => groupLabel(v, 'week_sum'),
      }
    : {
        title: 'Month', dataIndex: 'GroupKey', key: 'GroupKey', width: 110,
        sorter: (a, b) => String(a.GroupKey).localeCompare(String(b.GroupKey)),
        defaultSortOrder: 'ascend',
        render: v => groupLabel(v, 'month'),
      };

  const billableCol = {
    title: 'Billable', key: '_billable', width: 90, align: 'right',
    sorter: (a, b) => billable(a) - billable(b),
    render: (_, r) => fmtHrs(billable(r)),
  };

  const nonBillableCol = {
    title: 'Non-Billable', key: '_nonbillable', width: 105, align: 'right',
    sorter: (a, b) => nonBillable(a, codes) - nonBillable(b, codes),
    render: (_, r) => fmtHrs(nonBillable(r, codes)),
  };

  const forecastPerWeekdayCol = groupBy === 'month' ? {
    title: (
      <Tooltip title="Forecasted hours divided by weekdays (Mon–Fri) in the month">
        Fcst/Weekday
      </Tooltip>
    ),
    key: '_fcastPerWkday', width: 115, align: 'right',
    sorter: (a, b) => {
      const wdA = weekdaysInMonth(a.GroupKey);
      const wdB = weekdaysInMonth(b.GroupKey);
      return ((a.Forecasted ?? billable(a)) / (wdA || 1)) - ((b.Forecasted ?? billable(b)) / (wdB || 1));
    },
    render: (_, r) => {
      const wd = weekdaysInMonth(r.GroupKey);
      const hrs = r.Forecasted ?? billable(r);
      if (!wd || !hrs) return <span style={{ color: '#ccc' }}>—</span>;
      const isProjected = r.Forecasted != null;
      return (
        <span style={{ color: isProjected ? '#d48806' : undefined, fontStyle: isProjected ? 'italic' : undefined }}>
          {(hrs / wd).toFixed(1)}
        </span>
      );
    },
  } : null;

  const forecastCol = groupBy === 'month' ? {
    title: (
      <Tooltip title="Projected Billable hours at month end, based on day-of-week averages from loaded data">
        Forecasted
      </Tooltip>
    ),
    key: '_forecasted', width: 115, align: 'right',
    sorter: (a, b) => (a.Forecasted ?? billable(a)) - (b.Forecasted ?? billable(b)),
    render: (_, r) => r.Forecasted != null
      ? <span style={{ color: '#d48806', fontStyle: 'italic' }}>{fmtHrs(r.Forecasted)}</span>
      : fmtHrs(billable(r)),
  } : null;

  // ── Pivot: GroupKey values as columns, all metrics stacked per cell ─────────
  // Applies to month, week_sum, and dow — NOT By Agent Weekly (week).
  const pivotKeys = useMemo(() => {
    if (!pivotView || groupBy === 'week') return [];
    const keys = [...new Set(visibleRows.map(r => r.GroupKey))];
    if (groupBy === 'dow') return keys.sort((a, b) => (DOW_ORDER[a] ?? 9) - (DOW_ORDER[b] ?? 9));
    return keys.sort();
  }, [pivotView, groupBy, visibleRows]);

  const pivotRows = useMemo(() => {
    if (!pivotView || groupBy === 'week') return [];
    const groups = {};
    for (const r of visibleRows) {
      const key = `${r.DeptCode}||${r.TeamName}||${r.JobName}`;
      if (!groups[key]) groups[key] = { DeptCode: r.DeptCode, TeamName: r.TeamName, JobName: r.JobName };
      groups[key][r.GroupKey] = r;
    }
    return Object.values(groups).sort((a, b) =>
      (a.TeamName || '').localeCompare(b.TeamName || '') ||
      (a.JobName  || '').localeCompare(b.JobName  || '')
    );
  }, [pivotView, groupBy, visibleRows]);

  const pivotColumns = useMemo(() => {
    if (!pivotView || groupBy === 'week') return [];
    const showForecast = groupBy === 'month' && codes.some(c => ['Reg','OT','Hol1.5'].includes(c));
    const metricRows = [
      ...(showForecast ? [{ label: 'Forecasted', fn: r => r?.Forecasted ?? billable(r), fmt: fmtHrs, italic: true }] : []),
      { label: 'Billable',     fn: r => billable(r),            fmt: fmtHrs },
      { label: 'Non-Billable', fn: r => nonBillable(r, codes),  fmt: fmtHrs },
      ...codes.map(c => ({ label: c, fn: r => r?.[c] ?? 0, fmt: fmtHrs })),
    ];
    return [
      { title: 'Dept',  dataIndex: 'DeptCode',  key: 'DeptCode',  width: 80,  fixed: 'left' },
      { title: 'Team',  dataIndex: 'TeamName',  key: 'TeamName',  width: 160, fixed: 'left',
        render: v => <Typography.Text strong>{v}</Typography.Text> },
      { title: 'Job',   dataIndex: 'JobName',   key: 'JobName',   width: 140, fixed: 'left' },
      ...pivotKeys.map(gk => ({
        title: groupLabel(gk, groupBy), key: gk, align: 'right', width: 130,
        render: (_, row) => {
          const cell = row[gk];
          return (
            <div style={{ lineHeight: 1.7, fontSize: 12 }}>
              {metricRows.map(({ label, fn, fmt: f, italic }) => {
                const v = fn(cell);
                return (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ color: '#8c8c8c' }}>{label}</span>
                    <span style={{ fontStyle: italic && cell?.Forecasted != null ? 'italic' : undefined,
                                   color: italic && cell?.Forecasted != null ? '#d48806' : undefined }}>
                      {f(v)}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        },
      })),
    ];
  }, [pivotView, groupBy, pivotKeys, codes, visibleRows]);

  const columns = [
    {
      title: 'Dept Code', dataIndex: 'DeptCode', key: 'DeptCode', width: 110, fixed: 'left',
      sorter: (a, b) => (a.DeptCode || '').localeCompare(b.DeptCode || ''),
    },
    {
      title: 'Team', dataIndex: 'TeamName', key: 'TeamName', width: 180, fixed: 'left',
      sorter: (a, b) => (a.TeamName || '').localeCompare(b.TeamName || ''),
      render: v => <Typography.Text strong>{v}</Typography.Text>,
    },
    {
      title: 'Job', dataIndex: 'JobName', key: 'JobName', width: 160,
      sorter: (a, b) => (a.JobName || '').localeCompare(b.JobName || ''),
    },
    groupCol,
    ...(forecastPerWeekdayCol ? [forecastPerWeekdayCol] : []),
    ...(forecastCol ? [forecastCol] : []),
    billableCol,
    nonBillableCol,
    ...codes.map(code => ({
      title:     code,
      key:       code,
      dataIndex: code,
      width:     90,
      align:     'right',
      sorter:    (a, b) => (a[code] || 0) - (b[code] || 0),
      render:    v => fmtHrs(v),
    })),
  ];

  const agentColumns = [
    {
      title: 'Agent', dataIndex: 'AgentName', key: 'AgentName', width: 220, fixed: 'left',
      sorter: (a, b) => (a.AgentName || '').localeCompare(b.AgentName || ''),
      render: v => <Typography.Text strong>{v}</Typography.Text>,
    },
    {
      title: 'Team', dataIndex: 'TeamName', key: 'TeamName', width: 180,
      sorter: (a, b) => (a.TeamName || '').localeCompare(b.TeamName || ''),
    },
    {
      title: 'FT/PT', dataIndex: 'EmploymentType', key: 'EmploymentType', width: 80,
      sorter: (a, b) => (a.EmploymentType || '').localeCompare(b.EmploymentType || ''),
      render: v => v || '—',
    },
    {
      // From the effective hire date (the later of hire and rehire) to today,
      // the same figure the agent summary header shows.
      title: 'Tenure', dataIndex: 'HireDate', key: 'Tenure', width: 90,
      sorter: (a, b) => (b.HireDate || '9999').localeCompare(a.HireDate || '9999'),
      render: v => fmtTenure(v) ?? '—',
    },
    billableCol,
    nonBillableCol,
    ...codes.map(code => ({
      title: code, key: code, dataIndex: code, width: 90, align: 'right',
      sorter: (a, b) => (a[code] || 0) - (b[code] || 0),
      render: v => fmtHrs(v),
    })),
  ];

  return (
    <>
      <Typography.Title level={4} style={{ margin: '0 0 16px' }}>Time Card Summary</Typography.Title>

      <Space wrap style={{ marginBottom: 20 }}>
        <DatePicker
          picker="month" value={fromMonth} onChange={v => v && setFromMonth(v)}
          allowClear={false} placeholder="From month" format="MMM YYYY"
          disabledDate={d => toMonth && d.isAfter(toMonth, 'month')}
        />
        <DatePicker
          picker="month" value={toMonth} onChange={v => v && setToMonth(v)}
          allowClear={false} placeholder="To month" format="MMM YYYY"
          disabledDate={d => fromMonth && d.isBefore(fromMonth, 'month')}
        />
        <Segmented
          value={groupBy}
          onChange={v => { setGroupBy(v); setPivotView(false); }}
          options={[
            { label: 'By Month',         value: 'month'    },
            { label: 'By Week',          value: 'week_sum' },
            { label: 'By Day of Week',   value: 'dow'      },
            { label: 'By Agent',         value: 'agent'    },
            { label: 'Agent Hrs Weekly', value: 'week'     },
            { label: 'Odd Data',         value: 'odd'      },
          ]}
        />
        {/* By Agent has no date columns to pivot on, and Agent Hrs Weekly is
            already a pivot */}
        {groupBy === 'agent' && (
          <Input.Search
            allowClear
            placeholder="Search agent"
            value={agentSearch}
            onChange={e => setAgentSearch(e.target.value)}
            style={{ width: 200 }}
          />
        )}
        {groupBy !== 'week' && groupBy !== 'agent' && groupBy !== 'odd' && (
          <Segmented
            value={pivotView ? 'Pivot' : 'Table'}
            onChange={v => setPivotView(v === 'Pivot')}
            options={['Table', 'Pivot']}
            size="small"
          />
        )}
        <Select
          mode="multiple" allowClear placeholder="Dept Code"
          options={deptOptions} value={deptFilter} onChange={setDeptFilter}
          style={{ minWidth: 160 }} maxTagCount="responsive"
        />
        <Select
          mode="multiple" allowClear placeholder="Job"
          options={jobOptions} value={jobFilter} onChange={setJobFilter}
          style={{ minWidth: 200 }} maxTagCount="responsive"
        />
        <Checkbox checked={bucketTraining} onChange={e => setBucketTraining(e.target.checked)}>
          Bucket first 2 weeks as Training
        </Checkbox>
        {groupBy === 'week' && (
          <Space size={4}>
            <Select
              value={thresholdDir}
              onChange={setThresholdDir}
              style={{ width: 80 }}
              options={[
                { label: 'Under', value: 'under' },
                { label: 'Over',  value: 'over'  },
              ]}
              size="small"
            />
            <InputNumber
              value={hrsThreshold}
              onChange={v => v != null && setHrsThreshold(v)}
              min={1} max={80} step={1}
              addonAfter="hrs"
              size="small"
              style={{ width: 110 }}
            />
            <Checkbox checked={flaggedOnly} onChange={e => setFlaggedOnly(e.target.checked)}>
              Flagged only
            </Checkbox>
          </Space>
        )}
        {groupBy === 'odd' ? (
          <Button icon={<DownloadOutlined />} onClick={exportOdd} disabled={!oddRows.length}>
            Export
          </Button>
        ) : groupBy === 'week' ? (
          <Button icon={<DownloadOutlined />} onClick={exportWeekly} disabled={!weeklyPivot.rows.length}>
            Export
          </Button>
        ) : (<>
          <Button icon={<DownloadOutlined />} onClick={exportSummary} disabled={!visibleRows.length}>
            Export Summary
          </Button>
          <Button icon={<DownloadOutlined />} onClick={exportDetail} disabled={!detailRows.length}>
            Export Detail
          </Button>
        </>)}
      </Space>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
      ) : groupBy === 'agent' ? (
        visibleAgentRows.length === 0
          ? <Empty description="No timecard data for the selected period." style={{ padding: 40 }} />
          : <Table
              rowKey="EmployeeNumber"
              dataSource={visibleAgentRows}
              columns={agentColumns}
              size="small"
              pagination={false}
              sticky
              scroll={{ x: 'max-content' }}
              summary={() => {
                const totBillable    = visibleAgentRows.reduce((s, r) => s + billable(r), 0);
                const totNonBillable = visibleAgentRows.reduce((s, r) => s + nonBillable(r, codes), 0);
                let i = 0;
                const cells = [
                  // Spans Agent, Team, FT/PT and Tenure -- the columns
                  // before the first hours column.
                  <Table.Summary.Cell key="lead" index={0} colSpan={4}>
                    <strong>Total — {visibleAgentRows.length} agents</strong>
                  </Table.Summary.Cell>,
                ];
                i = 4;
                const push = node => {
                  cells.push(
                    <Table.Summary.Cell key={i} index={i} align="right">
                      <strong>{node}</strong>
                    </Table.Summary.Cell>
                  );
                  i += 1;
                };
                push(fmtHrs(totBillable));
                push(fmtHrs(totNonBillable));
                codes.forEach(c =>
                  push(fmtHrs(visibleAgentRows.reduce((s, r) => s + (r[c] || 0), 0))));
                return (
                  <Table.Summary fixed="top">
                    <Table.Summary.Row>{cells}</Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
      ) : groupBy === 'odd' ? (
        oddRows.length === 0
          ? <Empty description="No odd timecard data for the selected period." style={{ padding: 40 }} />
          : <Table
              rowKey="key"
              dataSource={oddRows}
              size="small"
              pagination={false}
              sticky
              scroll={{ x: 'max-content' }}
              columns={[
                { title: 'Date', dataIndex: 'Date', key: 'Date', width: 120, fixed: 'left',
                  sorter: (a, b) => a.Date.localeCompare(b.Date),
                  render: v => dayjs(v).format('ddd MMM D, YYYY') },
                { title: 'Agent', dataIndex: 'AgentName', key: 'AgentName', width: 190, fixed: 'left',
                  sorter: (a, b) => (a.AgentName || '').localeCompare(b.AgentName || '') },
                { title: 'Emp #', dataIndex: 'EmployeeNumber', key: 'EmployeeNumber', width: 80 },
                { title: 'Team', dataIndex: 'TeamName', key: 'TeamName', width: 150,
                  sorter: (a, b) => (a.TeamName || '').localeCompare(b.TeamName || '') },
                { title: 'Job', dataIndex: 'JobName', key: 'JobName', width: 140,
                  sorter: (a, b) => (a.JobName || '').localeCompare(b.JobName || '') },
                ...codes.map(c => ({
                  title: c, key: c, width: 70, align: 'right',
                  sorter: (a, b) => (a[c] || 0) - (b[c] || 0),
                  render: (_, r) => fmtHrs(r[c]),
                })),
                { title: 'Total', dataIndex: 'Total', key: 'Total', width: 80, align: 'right',
                  sorter: (a, b) => a.Total - b.Total,
                  render: v => <span style={{ color: '#ff4d4f', fontWeight: 600 }}>{fmtHrs(v)}</span> },
                { title: 'Scheduled', dataIndex: 'Scheduled', key: 'Scheduled', width: 90, align: 'right',
                  sorter: (a, b) => (a.Scheduled ?? -1) - (b.Scheduled ?? -1),
                  render: v => (v == null ? 'None' : fmtHrs(v)) },
                { title: 'Issue', key: 'Issues', render: (_, r) => r.Issues.join('; ') },
              ]}
            />
      ) : groupBy === 'week' ? (
        weeklyPivot.rows.length === 0
          ? <Empty description="No timecard data for the selected period." style={{ padding: 40 }} />
          : <Table
              rowKey="key"
              dataSource={weeklyPivot.rows}
              size="small"
              pagination={false}
              sticky
              scroll={{ x: 'max-content' }}
              columns={[
                { title: 'Agent', dataIndex: 'AgentName', key: 'AgentName', width: 210, fixed: 'left',
                  sorter: (a, b) => (a.AgentName || '').localeCompare(b.AgentName || ''),
                  render: (v, r) => r._activeWeeks > 0
                    ? <span>{v} <span style={{ color: r._flaggedWeeks > 0 ? '#fa8c16' : undefined, fontSize: 12 }}>({r._flaggedWeeks}/{r._activeWeeks})</span></span>
                    : v },
                { title: 'Team',  dataIndex: 'TeamName',  key: 'TeamName',  width: 160, fixed: 'left',
                  sorter: (a, b) => (a.TeamName || '').localeCompare(b.TeamName || '') },
                { title: 'Job',   dataIndex: 'JobName',   key: 'JobName',   width: 140,
                  sorter: (a, b) => (a.JobName || '').localeCompare(b.JobName || '') },
                { title: 'FT/PT', dataIndex: 'EmploymentType', key: 'EmploymentType', width: 70,
                  sorter: (a, b) => (a.EmploymentType || '').localeCompare(b.EmploymentType || ''),
                  render: v => v || '—' },
                ...weeklyPivot.weeks.map(wk => ({
                  title:  groupLabel(wk, 'week'),
                  key:    wk,
                  width:  110,
                  align:  'right',
                  sorter: (a, b) => (a[wk] || 0) - (b[wk] || 0),
                  render: (_, r) => {
                    const hrs = r[wk] || 0;
                    const flagged = weeklyPivot.isFlagged(hrs);
                    const color = flagged
                      ? (thresholdDir === 'under'
                          ? (hrs < hrsThreshold - 5 ? '#ff4d4f' : '#fa8c16')
                          : (hrs > hrsThreshold + 5 ? '#ff4d4f' : '#fa8c16'))
                      : undefined;
                    return (
                      <span style={{ color, fontWeight: flagged ? 600 : undefined }}>
                        {fmtHrs(hrs)}
                      </span>
                    );
                  },
                })),
              ]}
            />
      ) : visibleRows.length === 0 ? (
        <Empty description="No timecard data for the selected period." style={{ padding: 40 }} />
      ) : pivotView && groupBy !== 'week' ? (
        <Table
          rowKey={r => `${r.DeptCode}||${r.TeamName}||${r.JobName}`}
          dataSource={pivotRows}
          columns={pivotColumns}
          size="small"
          pagination={false}
          sticky
          scroll={{ x: 'max-content' }}
        />
      ) : (
        <Table
          rowKey={r => `${r.DeptCode}-${r.JobName}-${r.GroupKey}`}
          dataSource={visibleRows}
          columns={columns}
          size="small"
          pagination={false}
          sticky
          scroll={{ x: 'max-content' }}
          summary={() => {
            if (groupBy === 'dow' || groupBy === 'week') return null;
            const totBillable    = visibleRows.reduce((s, r) => s + billable(r), 0);
            const totNonBillable = visibleRows.reduce((s, r) => s + nonBillable(r, codes), 0);
            const totForecast    = forecastCol
              ? visibleRows.reduce((s, r) => s + (r.Forecasted ?? billable(r)), 0) : null;
            const cells = [];
            let idx = 0;
            cells.push(
              <Table.Summary.Cell key="id" index={idx} colSpan={3} style={{ fontWeight: 600 }}>Total</Table.Summary.Cell>
            );
            idx += 3;
            cells.push(<Table.Summary.Cell key="grp" index={idx++} />);
            if (forecastPerWeekdayCol) cells.push(<Table.Summary.Cell key="fpw" index={idx++} />);
            if (forecastCol) cells.push(
              <Table.Summary.Cell key="fc" index={idx++} align="right" style={{ fontWeight: 600 }}>
                {fmtHrs(totForecast)}
              </Table.Summary.Cell>
            );
            cells.push(
              <Table.Summary.Cell key="bil"  index={idx++} align="right" style={{ fontWeight: 600 }}>{fmtHrs(totBillable)}</Table.Summary.Cell>,
              <Table.Summary.Cell key="nbil" index={idx++} align="right" style={{ fontWeight: 600 }}>{fmtHrs(totNonBillable)}</Table.Summary.Cell>,
            );
            codes.forEach(c => {
              const tot = visibleRows.reduce((s, r) => s + (r[c] || 0), 0);
              cells.push(
                <Table.Summary.Cell key={c} index={idx++} align="right" style={{ fontWeight: 600 }}>
                  {fmtHrs(tot)}
                </Table.Summary.Cell>
              );
            });
            return (
              <Table.Summary fixed="top">
                <Table.Summary.Row>{cells}</Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      )}
    </>
  );
}
